import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import SteamEffect from '@/components/effects/SteamEffect';

export default function MissionSection() {
  const { t } = useTranslation();
  const [videoOpen, setVideoOpen] = useState(false);

  return (
    <section id="about" className="relative py-20 md:py-28 bg-coffee-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Video Thumbnail */}
          <motion.div
            className="relative rounded-2xl overflow-hidden shadow-2xl"
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.7 }}
          >
            <img
              src="/images/video-bg.jpg"
              alt={t('mission.title')}
              className="w-full aspect-video object-cover"
            />
            <button
              onClick={() => setVideoOpen(true)}
              className="absolute inset-0 flex items-center justify-center group"
              aria-label={t('mission.play')}
            >
              <div className="w-16 h-16 md:w-20 md:h-20 bg-white/90 backdrop-blur rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                <Play className="w-7 h-7 text-coffee-700 fill-current ml-1" />
              </div>
            </button>
            <SteamEffect />
          </motion.div>

          {/* Text Content */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.7, delay: 0.15 }}
          >
            <span className="inline-block uppercase tracking-widest text-sm font-semibold text-coffee-400 mb-3">
              {t('mission.label')}
            </span>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-coffee-900 mb-6">
              {t('mission.title')}
            </h2>

            <p className="text-lg md:text-xl font-display text-coffee-700 italic mb-6 leading-relaxed">
              "{t('mission.highlight')}"
            </p>

            <p className="text-base md:text-lg text-coffee-800 leading-relaxed">
              {t('mission.body')}
            </p>
          </motion.div>
        </div>
      </div>

      {/* Video Modal */}
      <AnimatePresence>
        {videoOpen && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[70]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setVideoOpen(false)}
            />
            <motion.div
              className="fixed inset-0 z-[80] flex items-center justify-center p-4"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
            >
              <div className="relative w-full max-w-4xl aspect-video rounded-2xl overflow-hidden shadow-2xl bg-black">
                <button
                  onClick={() => setVideoOpen(false)}
                  className="absolute top-3 right-3 z-10 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
                  aria-label="Cerrar video"
                >
                  <X className="w-5 h-5" />
                </button>
                <video
                  className="w-full h-full"
                  src="/videos/sauces_presentation.webm"
                  controls
                  autoPlay
                  playsInline
                />
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </section>
  );
}
